import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import LanguageWrapper, { LanguageRedirect } from "./components/LanguageWrapper";

// Critical route - loaded immediately
import Index from "./pages/Index";

// Lazy loaded routes for code splitting
const TripBuilder = lazy(() => import("./pages/TripBuilder"));
const MyTrips = lazy(() => import("./pages/MyTrips"));
const Profile = lazy(() => import("./pages/Profile"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const Explore = lazy(() => import("./pages/Explore"));
const Destination = lazy(() => import("./pages/Destination"));
const City = lazy(() => import("./pages/City"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const LegalNotice = lazy(() => import("./pages/LegalNotice"));
const UserGuide = lazy(() => import("./pages/UserGuide"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const BrandAssets = lazy(() => import("./pages/BrandAssets"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Admin = lazy(() => import("./pages/Admin"));
const About = lazy(() => import("./pages/About"));
const WorldMap = lazy(() => import("./pages/WorldMap"));
const Destinations = lazy(() => import("./pages/Destinations"));
const Story = lazy(() => import("./pages/Story"));
const Agencies = lazy(() => import("./pages/Agencies"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Paths that should be handled by Vercel rewrites, not React Router
const STATIC_FILE_PATHS = ['/sitemap.xml', '/sitemap-dynamic.xml', '/robots.txt'];

const App = () => {
  // Don't render React app for static files - let Vercel handle them
  if (STATIC_FILE_PATHS.includes(window.location.pathname)) {
    return null;
  }

  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Redirect root to language-prefixed route */}
            <Route path="/" element={<LanguageRedirect />} />
            
            {/* Language-prefixed routes */}
            <Route path="/:lang" element={<LanguageWrapper />}>
              <Route index element={<Index />} />
              <Route path="explore" element={<Explore />} />
              <Route path="destinations" element={<Destinations />} />
              <Route path="destination/:country" element={<Destination />} />
              <Route path="destination/:country/:city" element={<City />} />
              <Route path="my-trips" element={<MyTrips />} />
              <Route path="profile" element={<Profile />} />
              <Route path="profile/:username" element={<PublicProfile />} />
              <Route path="trip/:slugOrId" element={<TripBuilder />} />
              <Route path="guide" element={<UserGuide />} />
              <Route path="blog" element={<Blog />} />
              <Route path="blog/:id" element={<BlogPost />} />
              <Route path="admin" element={<Admin />} />
              <Route path="faq" element={<FAQ />} />
              <Route path="contact" element={<Contact />} />
              <Route path="legal/terms" element={<TermsOfService />} />
              <Route path="legal/privacy" element={<PrivacyPolicy />} />
              <Route path="legal/notice" element={<LegalNotice />} />
              <Route path="brand" element={<BrandAssets />} />
              <Route path="about" element={<About />} />
              <Route path="reset-password" element={<ResetPassword />} />
              <Route path="story" element={<Story />} />
              <Route path="map" element={<WorldMap />} />
              <Route path="agencies" element={<Agencies />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* Legacy routes redirect to language-prefixed */}
            <Route path="/explore" element={<LanguageRedirect />} />
            <Route path="/my-trips" element={<LanguageRedirect />} />
            <Route path="/profile" element={<LanguageRedirect />} />
            <Route path="/trip/:slugOrId" element={<LanguageRedirect />} />
            <Route path="/guide" element={<LanguageRedirect />} />
            <Route path="/blog" element={<LanguageRedirect />} />
            <Route path="/blog/:id" element={<LanguageRedirect />} />
            <Route path="/faq" element={<LanguageRedirect />} />
            <Route path="/contact" element={<LanguageRedirect />} />
            <Route path="/legal/*" element={<LanguageRedirect />} />
            <Route path="/brand" element={<LanguageRedirect />} />
            <Route path="/reset-password" element={<LanguageRedirect />} />
            <Route path="/blog-admin" element={<LanguageRedirect />} />
            
            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
